import { useMemo } from "react";
import type { GetProjectFilesResponseBody } from "@imagine/ai-service/shared-types/route-types";
import { isReadOnlyPath } from "@/lib/read-only-files";
import type { CodeEditorDraftEntry } from "@/stores/slices/code-editor.slice";

export type ProjectFile = GetProjectFilesResponseBody["files"][number];
export type DraftProjectFile = ProjectFile & { isDraft?: boolean };

type UseCodePanelFilesParams = {
  apiFiles: Array<ProjectFile> | undefined;
  drafts: Record<string, CodeEditorDraftEntry>;
};

export function useCodePanelFiles({
  apiFiles,
  drafts,
}: UseCodePanelFilesParams) {
  const serverFiles = useMemo(() => {
    const list = apiFiles ?? [];
    return list.map((file) => ({
      ...file,
      readOnly:
        typeof file.readOnly === "boolean"
          ? file.readOnly
          : isReadOnlyPath(file.path),
    }));
  }, [apiFiles]);

  const serverFilePaths = useMemo(() => {
    const set = new Set<string>();
    for (const file of serverFiles) {
      set.add(file.path);
    }
    return set;
  }, [serverFiles]);

  const combinedFiles = useMemo(() => {
    const draftFiles: Array<DraftProjectFile> = [];
    for (const [path, draft] of Object.entries(drafts)) {
      if (!serverFilePaths.has(path)) {
        draftFiles.push({
          path,
          content: draft.content,
          readOnly: false,
          isDraft: true,
        });
      }
    }
    return [...serverFiles, ...draftFiles];
  }, [serverFiles, drafts, serverFilePaths]);

  const fileMap = useMemo(() => {
    const map = new Map<string, DraftProjectFile>();
    for (const file of combinedFiles) {
      map.set(file.path, file);
    }
    return map;
  }, [combinedFiles]);

  return { serverFiles, serverFilePaths, combinedFiles, fileMap };
}
